import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Save, Loader2, Pin, MessageSquarePlus, Undo2 } from 'lucide-react';
import axios from 'axios';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { html } from '@codemirror/lang-html';
import { css } from '@codemirror/lang-css';
import { json } from '@codemirror/lang-json';
import { markdown } from '@codemirror/lang-markdown';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorView, Decoration, keymap, ViewPlugin, WidgetType } from '@codemirror/view';
import { StateEffect, StateField, Prec } from '@codemirror/state';
import { search, searchKeymap } from '@codemirror/search';
import {
  autocompletion,
  completionKeymap,
  closeBrackets,
  closeBracketsKeymap,
  startCompletion,
} from '@codemirror/autocomplete';

const authHeaders = () => {
  const token = localStorage.getItem('authToken') || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/** Markierten Code an den Agent-Chat schicken (wie Cursor „Add to Chat“) */
export function emitAddCodeToChat(ref) {
  try {
    window.dispatchEvent(new CustomEvent('odium:agent-add-code-ref', { detail: ref }));
  } catch (_) {
    /* ignore */
  }
}

function selectionToCodeRef(view, filePath) {
  if (!view || !filePath) return null;
  const sel = view.state.selection.main;
  if (sel.empty) return null;
  const from = Math.min(sel.from, sel.to);
  const to = Math.max(sel.from, sel.to);
  const text = view.state.doc.sliceString(from, to);
  if (!String(text || '').trim()) return null;
  const startLine = view.state.doc.lineAt(from).number;
  const endLine = view.state.doc.lineAt(Math.max(from, to - 1)).number;
  return {
    id: `cr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    path: String(filePath).replace(/\\/g, '/').replace(/^\/+/, ''),
    startLine,
    endLine,
    text: String(text).slice(0, 12000),
  };
}

const editorTheme = EditorView.theme({
  '&': {
    height: '100%',
    maxHeight: '100%',
    fontSize: '13px',
  },
  '&.cm-editor': {
    height: '100%',
    maxHeight: '100%',
  },
  '&.cm-editor.cm-focused': {
    outline: 'none',
  },
  '.cm-scroller': {
    overflow: 'auto !important',
    fontFamily:
      '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    lineHeight: '1.6',
  },
  '.cm-content': {
    padding: '12px 0',
    caretColor: '#e2e8f0',
  },
  '.cm-cursor, .cm-dropCursor': {
    borderLeftWidth: '2px',
    borderLeftColor: '#e2e8f0',
  },
  '.cm-gutters': {
    backgroundColor: '#0c0f14',
    border: 'none',
  },
  '.cm-tooltip-autocomplete': {
    border: '1px solid #334155',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#111827',
    boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
  },
  '.cm-tooltip-autocomplete ul li[aria-selected]': {
    backgroundColor: 'rgba(16, 185, 129, 0.22)',
  },
  '.cm-completionIcon': {
    opacity: 0.55,
  },
  '.cm-completionMatchedText': {
    color: '#6ee7b7',
    textDecoration: 'none',
    fontWeight: 600,
  },
  '.cm-diffAdded': {
    backgroundColor: 'rgba(34, 197, 94, 0.14)',
    boxShadow: 'inset 3px 0 0 rgba(34, 197, 94, 0.9)',
  },
  '.cm-diffRemovedLine': {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0',
    backgroundColor: 'rgba(244, 63, 94, 0.14)',
    color: 'rgba(254, 202, 202, 0.95)',
    padding: '0 12px 0 0',
    margin: '0',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    lineHeight: 'inherit',
    whiteSpace: 'pre',
    boxShadow: 'inset 3px 0 0 rgba(244, 63, 94, 0.9)',
    opacity: '0.98',
  },
  '.cm-diffRemovedPrefix': {
    flex: '0 0 1.6rem',
    textAlign: 'center',
    color: 'rgba(251, 113, 133, 0.95)',
    userSelect: 'none',
    opacity: '0.9',
  },
  '.cm-diffRemovedText': {
    flex: '1 1 auto',
    minWidth: '0',
    textDecoration: 'none',
  },
  '.cm-diffAddedGutterSign': {
    color: 'rgba(74, 222, 128, 0.95)',
    fontSize: '11px',
    fontWeight: '600',
    paddingLeft: '2px',
  },
  '.cm-diffRemovedGutter': {
    backgroundColor: 'rgba(244, 63, 94, 0.2)',
  },
  '.cm-diffAddedGutter': {
    backgroundColor: 'rgba(34, 197, 94, 0.18)',
  },
  '.cm-agentCursorLine': {
    backgroundColor: 'rgba(56, 189, 248, 0.12)',
  },
  '.cm-diffAcceptBar': {
    // reserved for portal styles
  },
  '.cm-panel.cm-search': {
    backgroundColor: '#111827',
    borderBottom: '1px solid #334155',
    padding: '6px 8px',
  },
  '.cm-panel.cm-search input, .cm-panel.cm-search button': {
    font: 'inherit',
    color: '#e2e8f0',
  },
  '.cm-panel.cm-search input': {
    backgroundColor: '#0c0f14',
    border: '1px solid #334155',
    borderRadius: '6px',
    padding: '4px 8px',
  },
  '.cm-panel.cm-search button': {
    backgroundColor: '#1f2937',
    border: '1px solid #334155',
    borderRadius: '6px',
    padding: '4px 8px',
  },
  '.cm-panel.cm-search label': {
    color: '#94a3b8',
    fontSize: '12px',
  },
});

const setDiffLinesEffect = StateEffect.define();

/** Rote „Geisterzeile“ für entfernten Code (Cursor-Stil) */
class RemovedLineWidget extends WidgetType {
  constructor(text) {
    super();
    this.text = text;
  }

  eq(other) {
    return other instanceof RemovedLineWidget && other.text === this.text;
  }

  toDOM() {
    const el = document.createElement('div');
    el.className = 'cm-diffRemovedLine';
    el.setAttribute('aria-hidden', 'true');
    const prefix = document.createElement('span');
    prefix.className = 'cm-diffRemovedPrefix';
    prefix.textContent = '−';
    const body = document.createElement('span');
    body.className = 'cm-diffRemovedText';
    body.textContent = this.text || ' ';
    el.appendChild(prefix);
    el.appendChild(body);
    return el;
  }

  ignoreEvent() {
    return true;
  }
}

function buildInlineDiffDecorations(diff, doc) {
  const rows = Array.isArray(diff?.lines) ? diff.lines : [];
  const added = [];
  const removedBefore = [];
  let newLine = 0;
  let pendingRemoved = [];
  const hasLineNums = rows.some(
    (r) => r && (r.newLine != null || r.oldLine != null) && (r.op === '+' || r.op === ' ' || r.op === '-')
  );

  const flushRemoved = (beforeLine) => {
    if (!pendingRemoved.length) return;
    removedBefore.push({
      beforeLine: Math.max(1, Number(beforeLine) || 1),
      texts: [...pendingRemoved],
    });
    pendingRemoved = [];
  };

  if (diff?.created) {
    for (let i = 1; i <= doc.lines; i += 1) added.push(i);
  } else if (hasLineNums) {
    for (const row of rows) {
      const op = row?.op;
      const text = String(row?.text ?? '');
      if (op === 'meta') continue;
      if (op === '+') {
        const nl = Math.max(1, Number(row.newLine) || newLine + 1);
        flushRemoved(nl);
        newLine = nl;
        added.push(nl);
      } else if (op === '-') {
        pendingRemoved.push(text);
      } else if (op === ' ') {
        const nl = Math.max(1, Number(row.newLine) || newLine + 1);
        flushRemoved(nl);
        newLine = nl;
      }
    }
    flushRemoved(newLine + 1);
  } else {
    // Legacy-Fallback: Zeilen durchzählen (ungenau bei verdichtetem Diff)
    for (const row of rows) {
      const op = row?.op;
      const text = String(row?.text ?? '');
      if (op === 'meta') continue;
      if (op === '+') {
        flushRemoved(newLine + 1);
        newLine += 1;
        added.push(newLine);
      } else if (op === '-') {
        pendingRemoved.push(text);
      } else if (op === ' ') {
        flushRemoved(newLine + 1);
        newLine += 1;
      }
    }
    flushRemoved(newLine + 1);
  }

  const builder = [];
  for (const lineNo of added) {
    if (lineNo < 1 || lineNo > doc.lines) continue;
    const line = doc.line(lineNo);
    builder.push(Decoration.line({ class: 'cm-diffAdded' }).range(line.from));
  }

  for (const { beforeLine, texts } of removedBefore) {
    const clamped = Math.min(Math.max(1, beforeLine), doc.lines + 1);
    const pos = clamped > doc.lines ? doc.length : doc.line(clamped).from;
    for (const t of texts) {
      builder.push(
        Decoration.widget({
          widget: new RemovedLineWidget(t),
          block: true,
          side: -1,
        }).range(pos)
      );
    }
  }

  return { decorations: builder, added, removed: rows.filter((r) => r?.op === '-').length };
}

const diffDecorations = StateField.define({
  create() {
    return Decoration.none;
  },
  update(value, tr) {
    for (const e of tr.effects) {
      if (e.is(setDiffLinesEffect)) {
        const diff = e.value?.diff;
        const cursor = e.value?.cursor;
        const doc = tr.state.doc;
        const { decorations: builder } = buildInlineDiffDecorations(diff || {}, doc);
        if (cursor != null && cursor >= 1 && cursor <= doc.lines) {
          const line = doc.line(cursor);
          builder.push(Decoration.line({ class: 'cm-agentCursorLine' }).range(line.from));
        }
        return Decoration.set(builder, true);
      }
    }
    return value.map(tr.changes);
  },
  provide: (f) => EditorView.decorations.from(f),
});

function languageForPath(filePath) {
  const name = String(filePath || '').toLowerCase();
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')) : '';
  if (ext === '.tsx' || ext === '.ts') return javascript({ jsx: true, typescript: true });
  if (ext === '.jsx' || ext === '.js' || ext === '.mjs' || ext === '.cjs') {
    return javascript({ jsx: true });
  }
  if (ext === '.html' || ext === '.htm') return html({ autoCloseTags: true });
  if (ext === '.css' || ext === '.scss') return css();
  if (ext === '.json') return json();
  if (ext === '.md' || ext === '.markdown' || ext === '.mdx') return markdown();
  if (ext === '.svg') return html({ autoCloseTags: true });
  if (name.endsWith('package.json') || name.endsWith('tsconfig.json')) return json();
  return javascript({ jsx: true });
}

function languageLabelForPath(filePath) {
  const name = String(filePath || '').toLowerCase();
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1) : '';
  const map = {
    js: 'JavaScript',
    jsx: 'JavaScript',
    mjs: 'JavaScript',
    cjs: 'JavaScript',
    ts: 'TypeScript',
    tsx: 'TypeScript',
    html: 'HTML',
    htm: 'HTML',
    css: 'CSS',
    scss: 'SCSS',
    json: 'JSON',
    md: 'Markdown',
    markdown: 'Markdown',
    mdx: 'MDX',
    svg: 'SVG',
  };
  return map[ext] || (ext ? ext.toUpperCase() : 'Plain Text');
}

/**
 * Smooth catch-up: zeigt Agent-Schreiben als flüssiges Tippen,
 * holt bei großen Chunks schnell auf, ohne hart zu springen.
 */
function createSmoothWriter({ getDisplayed, setDisplayed, onTick }) {
  let target = '';
  let raf = null;
  let finishing = false;

  const stop = () => {
    if (raf != null) {
      cancelAnimationFrame(raf);
      raf = null;
    }
  };

  const tick = () => {
    raf = null;
    const current = getDisplayed();
    if (current === target) {
      finishing = false;
      onTick?.({ done: true, writing: false });
      return;
    }

    let next;
    if (target.startsWith(current)) {
      const remaining = target.length - current.length;
      // Wort-für-Wort: kleine Schritte, auch bei großem Rückstand nicht „durchrasen“
      const step = Math.min(
        remaining,
        Math.max(1, Math.min(6, Math.ceil(remaining / 48)))
      );
      next = target.slice(0, current.length + step);
    } else if (current.startsWith(target)) {
      // Kürzung: sanft zurücktrimmen
      const drop = Math.max(1, Math.ceil((current.length - target.length) / 10));
      next = current.slice(0, Math.max(target.length, current.length - drop));
    } else {
      // Find common prefix then rewrite smoothly from there
      let i = 0;
      const max = Math.min(current.length, target.length);
      while (i < max && current[i] === target[i]) i += 1;
      if (current.length > i) {
        next = current.slice(0, Math.max(i, current.length - Math.max(4, Math.ceil((current.length - i) / 8))));
      } else {
        const step = Math.max(2, Math.ceil((target.length - i) / 12));
        next = target.slice(0, i + step);
      }
    }

    setDisplayed(next);
    onTick?.({ done: false, writing: true });
    raf = requestAnimationFrame(tick);
  };

  return {
    setTarget(nextTarget, { immediate = false } = {}) {
      target = String(nextTarget ?? '');
      finishing = false;
      if (immediate) {
        stop();
        setDisplayed(target);
        onTick?.({ done: true, writing: false });
        return;
      }
      if (raf == null) raf = requestAnimationFrame(tick);
    },
    finish() {
      finishing = true;
      if (getDisplayed() === target) {
        onTick?.({ done: true, writing: false });
        return;
      }
      if (raf == null) raf = requestAnimationFrame(tick);
      // If already animating, tick will clear finishing when caught up
      void finishing;
    },
    snapToTarget() {
      stop();
      setDisplayed(target);
      finishing = false;
      onTick?.({ done: true, writing: false });
    },
    stop,
    getTarget: () => target,
  };
}

const AgentEditor = ({ projectId, filePath, onSaved, onPinFile, pinned = false, createHint = false }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [agentWriting, setAgentWriting] = useState(false);
  const [diffInfo, setDiffInfo] = useState(null);
  const [revisionId, setRevisionId] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [cursor, setCursor] = useState({ line: 1, col: 1 });
  const [selInfo, setSelInfo] = useState(null); // { startLine, endLine, top, left }
  const [chatFlash, setChatFlash] = useState(false);
  const viewRef = useRef(null);
  const livePathRef = useRef('');
  const skipNextLoadRef = useRef(false);
  const contentRef = useRef('');
  const loadedPathRef = useRef('');
  const smoothRef = useRef(null);
  const pendingDiffRef = useRef(null);
  const agentWritingRef = useRef(false);
  /** Nur mitlaufen, wenn Nutzer am unteren Rand ist (hochscrollen = Pause). */
  const stickBottomRef = useRef(true);
  const loadGenRef = useRef(0);
  const filePathRef = useRef(filePath);
  filePathRef.current = filePath;

  const addSelectionToChat = () => {
    const view = viewRef.current;
    const ref = selectionToCodeRef(view, filePathRef.current);
    if (!ref) return false;
    emitAddCodeToChat(ref);
    setChatFlash(true);
    window.setTimeout(() => setChatFlash(false), 900);
    try {
      window.dispatchEvent(
        new CustomEvent('odium:agent-workspace', { detail: { tab: 'editor' } })
      );
    } catch (_) {
      /* ignore */
    }
    // Mobile: Chat-Tab
    try {
      window.dispatchEvent(new CustomEvent('odium:agent-open-chat'));
    } catch (_) {
      /* ignore */
    }
    return true;
  };
  const addSelectionToChatRef = useRef(addSelectionToChat);
  addSelectionToChatRef.current = addSelectionToChat;

  contentRef.current = content;

  useEffect(() => {
    // Neue Datei: wieder mitfolgen, bis der Nutzer hochscrollt
    stickBottomRef.current = true;
    setSelInfo(null);
  }, [filePath]);

  useEffect(() => {
    smoothRef.current = createSmoothWriter({
      getDisplayed: () => contentRef.current,
      setDisplayed: (next) => {
        const text = String(next ?? '');
        contentRef.current = text;
        const view = viewRef.current;
        // Live-Schreiben: direkt im Editor updaten — kein setContent pro Frame,
        // sonst schließt Strg+F-Suche sofort wieder (controlled value reset).
        if (view && agentWritingRef.current) {
          requestAnimationFrame(() => {
            const v = viewRef.current;
            if (!v || !agentWritingRef.current) return;
            const cur = v.state.doc.toString();
            const lineNo = Math.max(1, v.state.doc.lines);
            const effects = setDiffLinesEffect.of({ diff: null, cursor: lineNo });
            if (cur !== text) {
              const spec = {
                changes: { from: 0, to: v.state.doc.length, insert: text },
                effects,
              };
              if (stickBottomRef.current) {
                spec.selection = { anchor: text.length };
                spec.scrollIntoView = true;
              }
              v.dispatch(spec);
            } else if (stickBottomRef.current) {
              v.dispatch({
                effects,
                selection: { anchor: text.length },
                scrollIntoView: true,
              });
            } else {
              v.dispatch({ effects });
            }
          });
          return;
        }
        setContent(text);
      },
      onTick: ({ done, writing }) => {
        if (writing) {
          if (!agentWritingRef.current) {
            agentWritingRef.current = true;
            setAgentWriting(true);
          }
          return;
        }
        if (done) {
          if (agentWritingRef.current) {
            agentWritingRef.current = false;
            setAgentWriting(false);
          }
          setContent(contentRef.current);
          const diff = pendingDiffRef.current;
          pendingDiffRef.current = null;
          if (diff) applyDiffHighlight(diff);
          else if (diff === null) clearDiffHighlight();
        }
      },
    });
    return () => smoothRef.current?.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fileName = useMemo(() => {
    const p = String(filePath || '').replace(/\\/g, '/');
    const parts = p.split('/').filter(Boolean);
    return parts[parts.length - 1] || p || 'untitled';
  }, [filePath]);

  const languageLabel = useMemo(() => languageLabelForPath(filePath), [filePath]);

  const extensions = useMemo(
    () => [
      languageForPath(filePath),
      editorTheme,
      search({ top: true }),
      EditorView.lineWrapping,
      diffDecorations,
      closeBrackets(),
      ViewPlugin.fromClass(
        class {
          constructor(view) {
            this.view = view;
            this.onScroll = () => {
              const el = view.scrollDOM;
              if (!el) return;
              const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
              stickBottomRef.current = dist < 48;
            };
            view.scrollDOM.addEventListener('scroll', this.onScroll, { passive: true });
          }
          destroy() {
            this.view.scrollDOM.removeEventListener('scroll', this.onScroll);
          }
        }
      ),
      autocompletion({
        activateOnTyping: true,
        activateOnTypingDelay: 80,
        maxRenderedOptions: 16,
        defaultKeymap: true,
        closeOnBlur: true,
        icons: true,
      }),
      Prec.highest(
        keymap.of([
          ...searchKeymap,
          ...closeBracketsKeymap,
          ...completionKeymap,
          {
            key: 'Ctrl-Space',
            mac: 'Cmd-Space',
            run: startCompletion,
          },
          {
            key: 'Mod-Shift-l',
            run: () => {
              addSelectionToChatRef.current?.();
              return true;
            },
          },
          {
            key: 'Mod-l',
            run: () => {
              // Wie Cursor: Auswahl → Chat (wenn markiert)
              const view = viewRef.current;
              if (view && !view.state.selection.main.empty) {
                addSelectionToChatRef.current?.();
                return true;
              }
              return false;
            },
          },
        ])
      ),
      EditorView.updateListener.of((update) => {
        if (!update.selectionSet && !update.docChanged && !update.geometryChanged) return;
        const head = update.state.selection.main.head;
        const line = update.state.doc.lineAt(head);
        setCursor({ line: line.number, col: head - line.from + 1 });

        const sel = update.state.selection.main;
        if (sel.empty) {
          setSelInfo(null);
          return;
        }
        const from = Math.min(sel.from, sel.to);
        const to = Math.max(sel.from, sel.to);
        if (!String(update.state.doc.sliceString(from, to) || '').trim()) {
          setSelInfo(null);
          return;
        }
        const startLine = update.state.doc.lineAt(from).number;
        const endLine = update.state.doc.lineAt(Math.max(from, to - 1)).number;
        try {
          const coords = update.view.coordsAtPos(from);
          const editorRect = update.view.dom.getBoundingClientRect();
          if (coords && editorRect) {
            setSelInfo({
              startLine,
              endLine,
              top: Math.max(8, coords.top - editorRect.top - 36),
              left: Math.min(
                Math.max(8, coords.left - editorRect.left),
                Math.max(8, editorRect.width - 160)
              ),
            });
          } else {
            setSelInfo({ startLine, endLine, top: 8, left: 8 });
          }
        } catch (_) {
          setSelInfo({ startLine, endLine, top: 8, left: 8 });
        }
      }),
    ],
    [filePath]
  );

  const applyDiffHighlight = (diff, nextRevisionId) => {
    const view = viewRef.current;
    if (!view) return;
    const { added, removed } = buildInlineDiffDecorations(diff, view.state.doc);
    view.dispatch({
      effects: setDiffLinesEffect.of({ diff, cursor: null }),
    });
    setDiffInfo({
      added: added.length,
      removed,
      created: !!diff?.created,
      unchanged: !!diff?.unchanged,
    });
    if (nextRevisionId) setRevisionId(String(nextRevisionId));

    // Erste Änderung ins Sichtfeld scrollen
    const firstLine = added[0];
    if (firstLine != null && firstLine >= 1) {
      try {
        const line = view.state.doc.line(Math.min(firstLine, view.state.doc.lines));
        view.dispatch({
          effects: EditorView.scrollIntoView(line.from, { y: 'center' }),
        });
      } catch (_) {
        /* ignore */
      }
    }
  };

  const clearDiffHighlight = () => {
    const view = viewRef.current;
    if (view) {
      try {
        view.dispatch({ effects: setDiffLinesEffect.of({ diff: null, cursor: null }) });
      } catch (_) {
        /* Editor beim Dateiwechsel ggf. schon neu aufgebaut */
      }
    }
    setDiffInfo(null);
    setRevisionId(null);
  };

  const acceptAgentChange = () => {
    clearDiffHighlight();
  };

  const rejectAgentChange = async () => {
    if (!projectId || rejecting) return;
    if (!revisionId) {
      // Kein Undo-Punkt → nur Highlight schließen
      clearDiffHighlight();
      return;
    }
    setRejecting(true);
    setError('');
    try {
      const res = await axios.post(
        `/api/ai/projects/${encodeURIComponent(projectId)}/file/restore`,
        { revisionId },
        { headers: authHeaders() }
      );
      const file = res.data?.file || res.data || {};
      const deleted = !!file.deleted;
      const restored = String(file.content ?? '');
      const restoredPath = String(file.path || file.restoredPath || filePath || '').replace(
        /\\/g,
        '/'
      );
      smoothRef.current?.stop();
      contentRef.current = restored;
      setContent(restored);
      setDirty(false);
      clearDiffHighlight();
      try {
        window.dispatchEvent(
          new CustomEvent('odium:agent-file-live', {
            detail: {
              projectId,
              path: restoredPath,
              content: restored,
              done: true,
              stage: 'done',
              rejected: true,
              deleted,
              diff: null,
            },
          })
        );
        window.dispatchEvent(
          new CustomEvent('odium:agent-change-rejected', {
            detail: { projectId, path: restoredPath, revisionId, deleted },
          })
        );
        if (deleted) {
          window.dispatchEvent(
            new CustomEvent('odium:agent-projects', {
              detail: { action: 'delete_path', path: restoredPath, projectId },
            })
          );
        }
      } catch (_) {
        /* ignore */
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Ablehnen fehlgeschlagen');
    } finally {
      setRejecting(false);
    }
  };

  useEffect(() => {
    const onLive = (ev) => {
      const d = ev?.detail || {};
      const path = String(d.path || '').replace(/\\/g, '/');
      const myPath = String(filePathRef.current || '').replace(/\\/g, '/');
      if (!path || !myPath || path !== myPath) return;
      if (d.projectId && projectId && String(d.projectId) !== String(projectId)) return;

      livePathRef.current = path;
      loadedPathRef.current = path;
      skipNextLoadRef.current = true;
      setDirty(false);
      setError('');
      setLoading(false);

      const isStreaming = d.stage === 'start' || (d.stage === 'write' && !d.done);
      const isDone = d.done || d.stage === 'done';
      const hasContent = Object.prototype.hasOwnProperty.call(d, 'content');
      const nextContent = hasContent ? String(d.content ?? '') : null;

      const markWriting = (on) => {
        if (agentWritingRef.current === on) return;
        agentWritingRef.current = on;
        setAgentWriting(on);
      };

      // Neuer Schreibvorgang: mitfolgen (Nutzer kann danach hochscrollen).
      // stage:start ist nur ein Signal: Datei öffnen + "schreibt..." zeigen.
      // Nicht leeren, sonst sieht eine bestehende Datei kaputt aus, bevor Content kommt.
      if (d.stage === 'start') {
        stickBottomRef.current = true;
        markWriting(true);
        if (hasContent && nextContent !== '') {
          smoothRef.current?.setTarget(nextContent);
        }
        return;
      }

      // Wichtig: done-Events OHNE content (z.B. project_changed) dürfen Target nicht auf "" setzen —
      // sonst wird die fertige Datei weg-animiert und „schreibt…“ flackert endlos.
      if (hasContent) {
        if (isStreaming || isDone) {
          markWriting(true);
          smoothRef.current?.setTarget(nextContent);
        } else {
          smoothRef.current?.setTarget(nextContent, { immediate: true });
          markWriting(false);
        }
      }

      if (isDone) {
        try {
          if (
            window.__odiumPendingLiveWrite &&
            String(window.__odiumPendingLiveWrite.path || '').replace(/\\/g, '/') === path
          ) {
            window.__odiumPendingLiveWrite = null;
          }
        } catch (_) {
          /* ignore */
        }
        if (d.diff) pendingDiffRef.current = d.diff;
        else if (d.diff === null) pendingDiffRef.current = null;
        const pendingRev = d.revisionId ? String(d.revisionId) : null;
        if (d.rejected) {
          pendingDiffRef.current = null;
        }

        const finalize = () => {
          smoothRef.current?.snapToTarget();
          markWriting(false);
          const diff = pendingDiffRef.current;
          pendingDiffRef.current = null;
          if (d.rejected) {
            clearDiffHighlight();
            return;
          }
          if (diff) applyDiffHighlight(diff, pendingRev);
          else if (diff === null) clearDiffHighlight();
          else if (pendingRev) setRevisionId(pendingRev);
        };

        if (!hasContent) {
          // Nur Diff/Status — Animation zum bestehenden Target beenden
          const target = smoothRef.current?.getTarget?.() ?? contentRef.current;
          if (contentRef.current === target) {
            finalize();
          } else {
            smoothRef.current?.finish();
            window.setTimeout(() => {
              if (String(filePathRef.current || '').replace(/\\/g, '/') !== path) return;
              finalize();
            }, 600);
          }
          return;
        }

        smoothRef.current?.finish();
        if (contentRef.current === nextContent) {
          finalize();
        } else {
          // Sicherheitsnetz: nie ewig auf „schreibt…“ hängen
          window.setTimeout(() => {
            if (String(filePathRef.current || '').replace(/\\/g, '/') !== path) return;
            if (!agentWritingRef.current) return;
            finalize();
          }, 900);
        }
      }
    };
    window.addEventListener('odium:agent-file-live', onLive);
    return () => window.removeEventListener('odium:agent-file-live', onLive);
  }, [projectId]);

  useEffect(() => {
    const gen = ++loadGenRef.current;
    const pathNorm = String(filePath || '').replace(/\\/g, '/');
    let fallbackTimer = null;
    const stillCurrent = () => loadGenRef.current === gen;

    let pending = null;
    try {
      pending = window.__odiumPendingLiveWrite;
    } catch (_) {
      pending = null;
    }
    const pendingMatches =
      pending &&
      String(pending.path || '').replace(/\\/g, '/') === pathNorm &&
      (!pending.projectId || String(pending.projectId) === String(projectId));

    // Wechsel ZUR Datei, die gerade gestreamt wird → Animation/Writing nicht killen
    if (!pendingMatches) {
      smoothRef.current?.stop();
      agentWritingRef.current = false;
      setAgentWriting(false);
      setDirty(false);
      setError('');
      clearDiffHighlight();
    }

    const applyLoadedText = (text, { dirty: nextDirty = false } = {}) => {
      if (!stillCurrent()) return;
      smoothRef.current?.stop();
      contentRef.current = text;
      loadedPathRef.current = pathNorm;
      setContent(text);
      setDirty(nextDirty);
      setAgentWriting(false);
      agentWritingRef.current = false;
      setLoading(false);
    };

    const loadFile = async () => {
      if (!stillCurrent()) return;
      setLoading(true);
      try {
        const res = await axios.get(`/api/ai/projects/${encodeURIComponent(projectId)}/file`, {
          headers: authHeaders(),
          params: { path: filePath },
          timeout: 12000,
        });
        if (!stillCurrent()) return;
        applyLoadedText(String(res.data?.file?.content || ''));
      } catch (err) {
        if (!stillCurrent()) return;
        if (createHint || err.response?.status === 400 || err.response?.status === 404) {
          applyLoadedText('', { dirty: !!createHint });
          setError('');
        } else {
          setError(err.response?.data?.error || err.message || 'Lesen fehlgeschlagen');
          setLoading(false);
        }
      }
    };

    if (!projectId || !filePath) {
      applyLoadedText('');
      loadedPathRef.current = '';
      return undefined;
    }

    // Nur skippen, wenn Live-Stream genau DIESE Datei gerade befüllt hat
    const skipLive =
      skipNextLoadRef.current &&
      livePathRef.current === pathNorm &&
      loadedPathRef.current === pathNorm;

    if (skipLive) {
      skipNextLoadRef.current = false;
      setLoading(false);
      return undefined;
    }

    skipNextLoadRef.current = false;

    const pendingHasLiveContent =
      pendingMatches &&
      pending.content != null &&
      String(pending.content || '') !== '';
    const pendingIsStartOnly =
      pendingMatches &&
      String(pending.stage || '').toLowerCase() === 'start' &&
      !pendingHasLiveContent;

    if (pendingMatches && !pendingIsStartOnly) {
      const buffered = String(pending.content || '');
      livePathRef.current = pathNorm;
      skipNextLoadRef.current = true;
      setLoading(false);
      setDirty(false);
      setError('');
      setAgentWriting(true);
      agentWritingRef.current = true;
      stickBottomRef.current = true;
      if (buffered) {
        contentRef.current = buffered;
        loadedPathRef.current = pathNorm;
        setContent(buffered);
        smoothRef.current?.setTarget(buffered);
      } else if (loadedPathRef.current !== pathNorm) {
        contentRef.current = '';
        loadedPathRef.current = pathNorm;
        setContent('');
      }
      fallbackTimer = setTimeout(() => {
        if (!stillCurrent()) return;
        try {
          if (
            window.__odiumPendingLiveWrite &&
            String(window.__odiumPendingLiveWrite.path || '').replace(/\\/g, '/') === pathNorm &&
            !agentWritingRef.current
          ) {
            window.__odiumPendingLiveWrite = null;
            loadFile();
          }
        } catch (_) {
          /* ignore */
        }
      }, 12000);
      return () => {
        if (fallbackTimer) clearTimeout(fallbackTimer);
      };
    }

    // Sofort leeren — sonst bleibt z.B. index.html unter Navigation.jsx stehen
    if (loadedPathRef.current !== pathNorm) {
      contentRef.current = '';
      loadedPathRef.current = '';
      setContent('');
    }

    loadFile();
    return () => {
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, [projectId, filePath, createHint]);

  const save = async () => {
    if (!projectId || !filePath) return;
    setSaving(true);
    setError('');
    try {
      await axios.put(
        `/api/ai/projects/${encodeURIComponent(projectId)}/file`,
        { path: filePath, content },
        { headers: authHeaders() }
      );
      setDirty(false);
      onSaved?.(filePath);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (dirty && !saving && !agentWriting) save();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, saving, content, projectId, filePath, agentWriting]);

  if (!projectId) {
    return (
      <div className="h-full flex items-center justify-center text-[12px] text-neutral-400 bg-[#0c0f14]">
        Projekt wählen
      </div>
    );
  }
  if (!filePath) {
    return (
      <div className="h-full flex items-center justify-center text-[12px] text-neutral-400 bg-[#0c0f14]">
        Datei im Baum öffnen
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 flex flex-col bg-[#0c0f14]">
      <div className="flex-shrink-0 flex items-center justify-between gap-2 px-2 py-1.5 border-b border-neutral-800 bg-neutral-950">
        <div className="min-w-0 flex items-center gap-2">
          <span className="text-[11px] font-mono truncate text-neutral-400" title={filePath}>
            {fileName}
            {dirty ? ' •' : ''}
          </span>
          {agentWriting ? (
            <span className="inline-flex items-center gap-1 text-[10px] text-sky-400 flex-shrink-0">
              <Loader2 className="w-3 h-3 animate-spin" />
              schreibt…
            </span>
          ) : null}
          {diffInfo && !agentWriting ? (
            <span className="inline-flex items-center gap-1.5 text-[10px] text-neutral-400 flex-shrink-0">
              {diffInfo.created ? (
                <span className="text-emerald-400">neu</span>
              ) : (
                <>
                  <span className="text-emerald-400">+{diffInfo.added}</span>
                  <span className="text-rose-400">−{diffInfo.removed}</span>
                </>
              )}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {typeof onPinFile === 'function' ? (
            <button
              type="button"
              onClick={() => onPinFile(filePath)}
              className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium border ${
                pinned
                  ? 'border-sky-500/40 bg-sky-500/15 text-sky-300'
                  : 'border-neutral-700 bg-neutral-900 text-neutral-300 hover:bg-neutral-800'
              }`}
              title={pinned ? 'Vom Chat lösen' : 'Im Chat anheften'}
            >
              <Pin className={`w-3 h-3 ${pinned ? 'fill-sky-400/40' : ''}`} />
              {pinned ? 'Angepinnt' : 'Anheften'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty || agentWriting}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium border border-neutral-700 bg-neutral-900 text-neutral-200 disabled:opacity-40 hover:bg-neutral-800"
            title="Speichern (Ctrl+S)"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Speichern
          </button>
        </div>
      </div>
      {error ? (
        <p className="px-2 py-1 text-[11px] text-rose-600 dark:text-rose-300">{error}</p>
      ) : null}
      <div className="flex-1 min-h-0 relative overflow-hidden bg-[#0c0f14]">
        <div className="absolute inset-0 [&_.cm-theme]:h-full [&_.cm-editor]:h-full [&_.cm-editor]:max-h-full">
          <CodeMirror
            key={filePath}
            value={content}
            height="100%"
            maxHeight="100%"
            theme={oneDark}
            extensions={extensions}
            editable={true}
            readOnly={agentWriting}
            style={{ height: '100%', maxHeight: '100%' }}
            basicSetup={{
              lineNumbers: true,
              foldGutter: true,
              highlightActiveLine: true,
              highlightSelectionMatches: true,
              bracketMatching: true,
              autocompletion: false,
              closeBrackets: false,
              completionKeymap: false,
              searchKeymap: false,
            }}
            onCreateEditor={(view) => {
              viewRef.current = view;
              const head = view.state.selection.main.head;
              const line = view.state.doc.lineAt(head);
              setCursor({ line: line.number, col: head - line.from + 1 });
            }}
            onChange={(value) => {
              if (agentWritingRef.current) return;
              contentRef.current = value;
              setContent(value);
              setDirty(true);
              clearDiffHighlight();
            }}
          />
        </div>
        {diffInfo && !agentWriting ? (
          <div className="absolute top-3 right-3 z-40 flex items-center gap-1 rounded-lg border border-neutral-600/70 bg-[#1a1d24]/95 px-1.5 py-1 shadow-xl backdrop-blur-md">
            <span className="text-[10px] text-neutral-400 tabular-nums px-1.5 border-r border-neutral-700/80">
              {diffInfo.created ? (
                <span className="text-emerald-400">neu</span>
              ) : (
                <>
                  <span className="text-emerald-400">+{diffInfo.added}</span>
                  <span className="text-neutral-600 mx-0.5">/</span>
                  <span className="text-rose-400">−{diffInfo.removed}</span>
                </>
              )}
            </span>
            <button
              type="button"
              onClick={rejectAgentChange}
              disabled={rejecting || (!revisionId && !diffInfo)}
              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12px] font-medium text-neutral-100 hover:bg-neutral-800/90 disabled:opacity-40"
              title="Änderung verwerfen"
            >
              {rejecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
              Ablehnen
            </button>
            <button
              type="button"
              onClick={acceptAgentChange}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-[11px] font-medium text-emerald-300 hover:bg-emerald-500/15"
              title="Änderung behalten"
            >
              Behalten
            </button>
          </div>
        ) : null}
        {selInfo && !agentWriting ? (
          <div
            className="absolute z-30 pointer-events-auto"
            style={{ top: selInfo.top, left: selInfo.left }}
          >
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                addSelectionToChat();
              }}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-medium shadow-lg transition-colors ${
                chatFlash
                  ? 'border-emerald-400/50 bg-emerald-500/25 text-emerald-100'
                  : 'border-sky-500/40 bg-neutral-950/95 text-sky-100 hover:bg-sky-500/20'
              }`}
              title="Markierung zum Chat (Ctrl+L / Ctrl+Shift+L)"
            >
              <MessageSquarePlus className="w-3.5 h-3.5" />
              Zum Chat
              <span className="text-[9px] text-neutral-400 tabular-nums">
                L{selInfo.startLine}
                {selInfo.endLine !== selInfo.startLine ? `–${selInfo.endLine}` : ''}
              </span>
            </button>
          </div>
        ) : null}
        {loading && !content ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#0c0f14]/60 pointer-events-none">
            <Loader2 className="w-4 h-4 animate-spin text-neutral-400" />
          </div>
        ) : null}
      </div>
      <div className="flex-shrink-0 flex items-center gap-2 px-2.5 h-6 border-t border-neutral-800 bg-neutral-950 text-[10px] text-neutral-500">
        <span>
          Ln {cursor.line}, Col {cursor.col}
        </span>
        <span className="text-neutral-700">|</span>
        <span>{languageLabel}</span>
        <span className="text-neutral-700">|</span>
        <span className="truncate min-w-0" title={filePath}>
          {filePath}
        </span>
        <span className="flex-1" />
        <span className={dirty ? 'text-amber-400/90' : 'text-neutral-600'}>
          {agentWriting ? 'Agent schreibt' : dirty ? 'Geändert' : 'Gespeichert'}
        </span>
        <span className="text-neutral-600 hidden sm:inline">Markieren → Zum Chat · Ctrl+L</span>
      </div>
    </div>
  );
};

export default AgentEditor;
